'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import {
  Button,
  ListPageHeader,
  ListPageTabs,
  Spinner,
} from '@terra/ui-design-system';
import { Database01Icon, GitForkIcon } from '@terra/ui-icons';
import { AiApplicationCompositionGraph } from '@terra/ui-ai-applications';
import { EmptyState } from '../EmptyState';
import { useIsMcpServerTarget, useTargetId } from '../../store/projectStore';
import { ASSETS_VIEW } from '../../store/tab/assetsTabScopedFilterStore';
import { AssetsTabWrapper } from './AssetsTabWrapper';
import { AiComponentsList } from './ai-assets/AiComponentsList';
import {
  AI_ASSETS_TABS,
  type AiAssetsTab,
} from './ai-assets/ai-components-table.model';
import { useAiComponentsFacet } from './ai-assets/useAiComponentsListData';
import { useAiApplicationComponents } from '../../hooks/api/useAiApplicationComponents';

const AI_ENDPOINT_VIEWS = [ASSETS_VIEW.ENDPOINTS];
const ALL_COMPONENTS_QUERY = {};

export function AiAssetsTabWrapper() {
  const targetId = useTargetId();
  const isMcpServerTarget = useIsMcpServerTarget();
  const t = useTranslations('aiApplications.assets');
  const tPage = useTranslations('proxy.assets.page');
  const tCopilot = useTranslations('chat.copilot');
  const tMainContent = useTranslations('proxy.mainContentArea');
  const tAssetList = useTranslations('internalNetwork.assetList');
  const [selectedTab, setSelectedTab] = useState<AiAssetsTab>('agents');
  const [showGraph, setShowGraph] = useState(false);
  const { segmentCounts } = useAiComponentsFacet({
    applicationId: targetId ?? '',
    query: ALL_COMPONENTS_QUERY,
    groupBy: 'type',
    enabled: !isMcpServerTarget,
  });
  const {
    components,
    relations,
    isLoading: isGraphLoading,
    isError: isGraphError,
  } = useAiApplicationComponents({
    aiApplicationId: targetId || null,
    enabled: showGraph && !isMcpServerTarget,
  });
  const tabOptions = useMemo(
    () =>
      AI_ASSETS_TABS.map((tab) => ({
        value: tab,
        label:
          tab === 'agents'
            ? t('agentsWithCount', { count: segmentCounts?.agents ?? 0 })
            : tab === 'components'
              ? t('componentsWithCount', {
                  count: segmentCounts?.components ?? 0,
                })
              : t('endpoints'),
      })),
    [segmentCounts, t],
  );

  if (!targetId) {
    return (
      <EmptyState
        icon={Database01Icon}
        title={tCopilot('noApplicationTitle')}
        description={tMainContent('selectApplicationToGetStarted')}
      />
    );
  }

  const graphLabel = t('viewGraph');
  const header = (
    <ListPageHeader
      title={tPage('title')}
      subtitle={tPage('aiApplicationSubtitle')}
      actions={
        isMcpServerTarget
          ? undefined
          : [
              <Button
                key="graph-view"
                size="xs"
                variant="outline"
                color="fg.accent.default"
                borderColor="border.accent.default"
                bg={showGraph ? 'bg.accent.light' : 'bg.canvas'}
                aria-pressed={showGraph}
                aria-label={graphLabel}
                onClick={() => setShowGraph((current) => !current)}
              >
                <GitForkIcon />
                {graphLabel}
              </Button>,
            ]
      }
    />
  );

  if (showGraph && !isMcpServerTarget) {
    return (
      <Box
        w="full"
        h="full"
        display="flex"
        flexDirection="column"
        bg="bg.default"
        overflow="hidden"
      >
        {header}
        <Box flex={1} minH={0} w="full">
          {isGraphLoading ? (
            <Box
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Box>
          ) : isGraphError ? (
            <EmptyState
              icon={Database01Icon}
              title={tAssetList('loadError')}
              description={tAssetList('loadErrorDescription')}
            />
          ) : (
            <AiApplicationCompositionGraph
              components={components}
              relations={relations}
              height="100%"
              persistentDetail
            />
          )}
        </Box>
      </Box>
    );
  }

  if (isMcpServerTarget) {
    return (
      <AiComponentsList
        applicationId={targetId}
        segment="components"
        isMcpApplication
        header={header}
      />
    );
  }

  const tabs = (
    <ListPageTabs
      options={tabOptions}
      value={selectedTab}
      onValueChange={setSelectedTab}
    />
  );

  if (selectedTab === 'endpoints') {
    return (
      <AssetsTabWrapper header={header} views={AI_ENDPOINT_VIEWS} tabs={tabs} />
    );
  }

  return (
    <AiComponentsList
      key={selectedTab}
      applicationId={targetId}
      segment={selectedTab}
      isMcpApplication={false}
      header={header}
      tabs={tabs}
    />
  );
}
